import axios, { AxiosError, AxiosHeaders, AxiosInstance } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api/v1';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '300000', 10);

// Create axios instance
// Default JSON Content-Type matches historical behavior for the app (plain-object bodies).
// For FormData, the request interceptor below removes Content-Type *before* axios
// transformRequest runs; otherwise transformRequest would stringify FormData as JSON
// and file uploads would break. Multipart requests then get the correct boundary from the browser.
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT, // timeout for long-running operations like fetching all pipelines
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = AxiosHeaders.from(config.headers);
      headers.delete('Content-Type');
      config.headers = headers;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Allow certain informational endpoints (e.g., survey stats) to fail with 401 without
    // forcing the user to be logged out. This prevents hard redirects during automated tests
    // where these endpoints may be intentionally unstubbed.
    if (error.response?.status === 401 && error.config?.url?.includes('/surveys/stats')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken, refreshToken: newRefreshToken } = response.data;
          localStorage.setItem('accessToken', accessToken);
          localStorage.setItem('refreshToken', newRefreshToken);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Redirect to login if refresh fails
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// API endpoints
export const authAPI = {
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  updatePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/auth/update-password', data),
  // Azure AD specific endpoints
  exchangeAzureToken: (data: { accessToken: string; id_token?: string }) =>
    api.post('/auth/azure/exchange', data),
  syncAzureUsers: () => api.post('/auth/azure/sync-users'),
  getAzureUserProfile: (accessToken: string) =>
    api.get('/auth/azure/profile', {
      headers: {
        'X-Azure-Token': accessToken,
      },
    }),
};

export const incidentAPI = {
  list: (params?: any) => api.get('/incidents', { params }),
  get: (id: string) => api.get(`/incidents/${id}`),
  create: (data: any) => api.post('/incidents', data),
  update: (id: string, data: any) => api.patch(`/incidents/${id}`, data),
  updateState: (id: string, state: string, resolution?: string) =>
    api.patch(`/incidents/${id}/state`, { state, resolution }),
  addNote: (id: string, note: string, isPublic: boolean = false) =>
    api.post(`/incidents/${id}/notes`, { note, isPublic }),
};

// Roles API
export const rolesAPI = {
  list: (params?: { search?: string; active?: boolean; page?: number; limit?: number }) => 
    api.get('/roles', { params }),
  get: (id: string) => api.get(`/roles/${id}`),
  create: (data: { name: string; description: string; permissions: any; isActive?: boolean }) =>
    api.post('/roles', data),
  update: (id: string, data: { name?: string; description?: string; permissions?: any; isActive?: boolean }) =>
    api.put(`/roles/${id}`, data),
  delete: (id: string) => api.delete(`/roles/${id}`),
  clone: (id: string, data: { name: string; description?: string }) =>
    api.post(`/roles/${id}/clone`, data),
  getUserRoles: (userId: string) => api.get(`/users/${userId}/custom-roles`),
  assignUserRoles: (userId: string, roleIds: string[], primaryRoleId?: string) =>
    api.post(`/users/${userId}/custom-roles`, { roleIds, primaryRoleId }),
  removeUserRole: (userId: string, roleId: string) =>
    api.delete(`/users/${userId}/custom-roles/${roleId}`),
};

// Notification API
export const notificationAPI = {
  // Email Templates
  templates: {
    list: (params?: { eventType?: string; entityType?: string; isActive?: boolean }) =>
      api.get('/email-templates', { params }),
    get: (id: string) => api.get(`/email-templates/${id}`),
    create: (data: any) => api.post('/email-templates', data),
    update: (id: string, data: any) => api.put(`/email-templates/${id}`, data),
    delete: (id: string) => api.delete(`/email-templates/${id}`),
    preview: (id: string, sampleData: any) =>
      api.post(`/email-templates/${id}/preview`, { sampleData }),
    test: (id: string, testEmail: string, sampleData: any) =>
      api.post(`/email-templates/${id}/test`, { testEmail, sampleData }),
    variables: (eventType: string) =>
      api.get(`/email-templates/variables/${eventType}`),
  },

  // Notification Rules
  rules: {
    list: (params?: { eventType?: string; entityType?: string; isActive?: boolean }) =>
      api.get('/notification-rules', { params }),
    get: (id: string) => api.get(`/notification-rules/${id}`),
    create: (data: any) => api.post('/notification-rules', data),
    update: (id: string, data: any) => api.put(`/notification-rules/${id}`, data),
    delete: (id: string) => api.delete(`/notification-rules/${id}`),
    test: (id: string, sampleData: any) =>
      api.post(`/notification-rules/${id}/test`, { sampleData }),
  },

  // Survey Email Templates
  surveyEmailTemplates: {
    list: (params?: { isActive?: boolean }) =>
      api.get('/survey-email-templates', { params }),
    get: (id: string) => api.get(`/survey-email-templates/${id}`),
    create: (data: any) => api.post('/survey-email-templates', data),
    update: (id: string, data: any) => api.put(`/survey-email-templates/${id}`, data),
    delete: (id: string) => api.delete(`/survey-email-templates/${id}`),
    preview: (id: string, sampleData: any) =>
      api.post(`/survey-email-templates/${id}/preview`, { sampleData }),
    variables: () => api.get('/survey-email-templates/variables'),
  },

  // Notification Logs
  logs: {
    list: (params?: {
      status?: string;
      eventType?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      limit?: number;
      offset?: number;
    }) => api.get('/notification-logs', { params }),
    get: (id: string) => api.get(`/notification-logs/${id}`),
    retry: (id: string) => api.post(`/notification-logs/${id}/retry`),
    stats: (params?: { dateFrom?: string; dateTo?: string; search?: string }) =>
      api.get('/notification-logs/stats', { params }),
    clearAll: (params?: { olderThan?: string; status?: string; entityType?: string }) =>
      api.delete('/notification-logs', { params }),
  },

  // User Notification Preferences
  preferences: {
    get: () => api.get('/user-notification-preferences'),
    update: (preferences: any) => api.put('/user-notification-preferences', preferences),
    getByUser: (userId: string) => api.get(`/user-notification-preferences/${userId}`),
  },
};

// List Layout Preferences API
export const listLayoutAPI = {
  get: (entityType: string) =>
    api.get(`/users/me/list-layout-preferences/${entityType}`),
  update: (entityType: string, columns: Array<{ key: string; visible: boolean; order: number }>) =>
    api.put(`/users/me/list-layout-preferences/${entityType}`, { columns }),
  reset: (entityType: string) =>
    api.delete(`/users/me/list-layout-preferences/${entityType}`),
};

// Filter Preferences API
export interface FilterPreferences {
  state?: string[];
  priority?: string[];
  severity?: string[];
  timeRange?: string;
  customer?: string;
  engagement?: string;
  category?: string;
  channel?: string;
  assignmentGroup?: string;
  assignedToMe?: boolean | string;
  assignedTo?: string;
  unassigned?: string;
  acknowledged?: string;
  // Time Entry specific filters
  type?: string[];
  status?: string[];
  month?: string;
  user?: string;
}

export const filterPreferencesAPI = {
  get: (entityType: string) =>
    api.get(`/users/me/filter-preferences/${entityType}`),
  update: (entityType: string, filters: FilterPreferences) =>
    api.put(`/users/me/filter-preferences/${entityType}`, { filters }),
  reset: (entityType: string) =>
    api.delete(`/users/me/filter-preferences/${entityType}`),
};

// Survey Templates API
export const surveyTemplatesAPI = {
  list: (params?: { active?: boolean; surveyType?: string; page?: number; limit?: number }) =>
    api.get('/survey-templates', { params }),
  get: (id: string) => api.get(`/survey-templates/${id}`),
  create: (data: {
    name: string;
    description?: string;
    surveyType?: string;
    active?: boolean;
    emailTemplateId?: string;
    questions: Array<{
      question: string;
      responseType: 'rating' | 'text' | 'choice' | 'multiple_choice' | 'boolean';
      required?: boolean;
      choices?: string[];
      order?: number;
    }>;
  }) => api.post('/survey-templates', data),
  update: (id: string, data: {
    name: string;
    description?: string;
    surveyType?: string;
    active?: boolean;
    emailTemplateId?: string;
    questions: Array<{
      id?: string; // Optional: for existing questions
      question: string;
      responseType: 'rating' | 'text' | 'choice' | 'multiple_choice' | 'boolean';
      required?: boolean;
      choices?: string[];
      order?: number;
    }>;
  }) => api.put(`/survey-templates/${id}`, data),
  delete: (id: string) => api.delete(`/survey-templates/${id}`),
  generatePublicLink: (id: string, emailId: string, ticketId: string) =>
    api.post(`/survey-templates/${id}/public-link`, { emailId, ticketId }),
  getPublicSurvey: (surveyId: string) =>
    api.get(`/public/survey/${surveyId}`),
  submitSurvey: (surveyId: string, responses: Record<string, any>) =>
    api.post(`/public/survey/${surveyId}/submit`, { responses }),
};

// Add more API endpoints for other modules...

export { api }; 